/**
 * A document as the source of something a person put in the memo (DOP-652):
 * naming it, showing it before it is saved, and sending it.
 *
 * What is kept in an edit draft is a PLACE: a file, and a page with its
 * passage or a workbook cell. The server re-reads that place when the version
 * is saved and builds the source the memo shows, so nothing here is a fact
 * about the document. The link this file builds is for opening the place
 * while the change is still a draft.
 */

import type { DealScreeningDocumentSource, DealScreeningSource } from "../../types/dealScreening";
import { parseDopplerUri } from "../../utils/dopplerUri";
import { FileType } from "../../types/fileType";

export type DocumentSource = DealScreeningDocumentSource;

/** The longest passage a link carries, as the server caps it. */
const HIGHLIGHT_MAX_CHARS = 200;
const CELL = /^[A-Z]{1,3}[1-9][0-9]{0,6}$/;

const clean = (text: unknown): string => String(text ?? "").replace(/\s+/g, " ").trim();

/** A place that names a file and one spot in it, or null. */
export function documentSource(raw: {
	attachment_id?: unknown;
	page?: unknown;
	sheet?: unknown;
	cell?: unknown;
	quote?: unknown;
	filename?: unknown;
}): DocumentSource | null {
	const id = Number(raw.attachment_id);
	if (!Number.isInteger(id) || id <= 0) return null;
	const filename = clean(raw.filename) || null;
	const quote = clean(raw.quote).slice(0, 2000);
	const sheet = clean(raw.sheet);
	const cell = clean(raw.cell).toUpperCase().replace(/\$/g, "");
	if (sheet && cell) {
		if (!CELL.test(cell)) return null;
		return { attachment_id: id, sheet, cell, page: null, quote, filename };
	}
	const page = Number(raw.page);
	if (Number.isInteger(page) && page >= 1 && quote) {
		return { attachment_id: id, page, sheet: null, cell: null, quote, filename };
	}
	return null;
}

/** A source the memo already shows, as the place it names. */
export function fromSavedSource(source: DealScreeningSource | null | undefined): DocumentSource | null {
	if (!source || (source.kind && source.kind !== "document")) return null;
	return documentSource(source);
}

/** What names the memo's files and sources, for reading a proposal's. */
export interface KnownSources {
	/** A file's name by its id. */
	filenameOf: (attachmentId: number) => string | null;
	/** A source the memo already cites, by its anchor. */
	byAnchor?: (anchor: string) => DealScreeningSource | null;
}

/**
 * A source as the memo chat writes it in a proposal: an anchor, or a file id
 * with a page and its passage, or a file id with a sheet and a cell.
 *
 * An anchor the memo already cites is that source, with its whole passage:
 * the anchor itself carries only the first words of it.
 */
export function fromProposalSource(
	raw: unknown,
	known: KnownSources["filenameOf"] | KnownSources = () => null,
): DocumentSource | null {
	if (!raw || typeof raw !== "object") return null;
	const { filenameOf, byAnchor }: KnownSources = typeof known === "function" ? { filenameOf: known } : known;
	const data = raw as Record<string, unknown>;
	let place: Parameters<typeof documentSource>[0] | null = null;
	if (typeof data.anchor === "string" && data.anchor) {
		const cited = fromSavedSource(byAnchor?.(data.anchor.trim()));
		if (cited) return cited;
		const parsed = parseDopplerUri(data.anchor.trim());
		if (!parsed) return null;
		if (parsed.kind === FileType.PDF) {
			place = { attachment_id: parsed.target, page: parsed.page, quote: data.quote ?? parsed.highlight };
		} else if (parsed.kind === FileType.EXCEL) {
			place = { attachment_id: parsed.target, sheet: parsed.sheet, cell: parsed.cell, quote: data.quote };
		} else {
			return null;
		}
	} else {
		place = {
			attachment_id: data.file_id ?? data.attachment_id,
			page: data.page,
			sheet: data.sheet,
			cell: data.cell,
			quote: data.quote,
		};
	}
	const found = documentSource(place);
	if (!found) return null;
	return { ...found, filename: clean(data.filename) || filenameOf(found.attachment_id) };
}

/** "Statements.pdf · p. 4" or "Model.xlsx · Summary!B8". */
export function documentSourceLabel(source: DocumentSource): string {
	const where = source.sheet && source.cell ? `${source.sheet}!${source.cell}` : `p. ${source.page}`;
	return `${source.filename || `File ${source.attachment_id}`} · ${where}`;
}

/** The link that opens the place in the viewer. */
export function documentSourceAnchor(source: DocumentSource): string {
	if (source.sheet && source.cell) {
		return `doppler://excel/${source.attachment_id}?sheet=${encodeURIComponent(source.sheet)}&cell=${source.cell}`;
	}
	const highlight = (source.quote ?? "").slice(0, HIGHLIGHT_MAX_CHARS);
	const base = `doppler://pdf/${source.attachment_id}?page=${source.page}`;
	return highlight ? `${base}&hl=${encodeURIComponent(highlight)}` : base;
}

/** The place as the save sends it: never the name, which is the server's to read. */
export function sentSource(source: DocumentSource): DocumentSource {
	return source.sheet && source.cell
		? { attachment_id: source.attachment_id, sheet: source.sheet, cell: source.cell, quote: source.quote ?? "" }
		: { attachment_id: source.attachment_id, page: source.page, quote: source.quote ?? "" };
}

/** Two places that are the same place. */
export const sourceKey = (source: DocumentSource | null | undefined): string =>
	source
		? [source.attachment_id, source.page ?? "", source.sheet ?? "", source.cell ?? "", source.quote ?? ""].join("\u0000")
		: "";

/** What a source reads as where a note would be: the person's note, else the document. */
export const sourceText = (note: string | null | undefined, source: DocumentSource | null | undefined): string =>
	clean(note) || (source ? documentSourceLabel(source) : "");
